import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import CompareSlider from "@/components/feature/CompareSlider";
import CompareCard from "@/components/feature/CompareCard";
import { creators } from "@/mocks/creators";

export default function Detail() {
  const { slug } = useParams<{ slug: string }>();
  const creator = creators.find((item) => item.slug === slug);

  if (!creator) {
    return (
      <SiteLayout>
        <Seo
          title="对比档案未找到 | 反差档案"
          description="该对比档案暂未收录，可返回榜单浏览其他网红的形象前后对比内容。"
          keywords="网红反差,网红前后对比"
          path="/bang"
        />
        <main className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
          <i className="ri-file-unknow-line text-5xl text-background-500"></i>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">
            这组对比档案还没收录
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            可能是链接有误，或者该对象的内容正在整理中。先去榜单看看其他收录对象吧。
          </p>
          <Link
            to="/bang"
            className="mt-7 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回对比榜单
          </Link>
        </main>
      </SiteLayout>
    );
  }

  const related = creators
    .filter((item) => item.slug !== creator.slug && item.categorySlug === creator.categorySlug)
    .concat(creators.filter((item) => item.slug !== creator.slug && item.categorySlug !== creator.categorySlug))
    .slice(0, 3);

  const stats = [
    { label: "早期节点", value: `${creator.beforeYear} 年` },
    { label: "近期节点", value: `${creator.afterYear} 年` },
    { label: "反差指数", value: String(creator.contrastScore) },
    { label: "全网粉丝", value: creator.fans },
    { label: "站内热度", value: String(creator.heat) },
  ];

  const articleJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: creator.title,
      description: creator.summary,
      inLanguage: "zh-CN",
      datePublished: `${creator.beforeYear}-01-01`,
      dateModified: `${creator.afterYear}-01-01`,
      author: { "@type": "Organization", name: "反差档案" },
      publisher: { "@type": "Organization", name: "反差档案" },
      about: `${creator.name}的网红反差前后形象对比`,
      articleSection: creator.category,
      keywords: [
        "网红反差",
        "网红反差前后对比",
        "网红反差变化有多大",
        creator.name,
        creator.category,
      ].join(","),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://fancha-archive.example/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "对比榜单",
          item: "https://fancha-archive.example/bang",
        },
        { "@type": "ListItem", position: 3, name: `${creator.name} 形象对比` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${creator.name}网红反差对比 - ${creator.name}前后形象变化有多大 | 反差档案`}
        description={`${creator.name}网红反差前后对比：${creator.summary.slice(0, 58)} 收录早期与近期公开画面，附形象变化时间线，直观呈现网红反差变化有多大。`}
        keywords={`${creator.name}网红反差,${creator.name}前后对比,网红反差变化有多大,${creator.category}博主形象对比`}
        path={`/detail/${creator.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />

      <main>
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "对比榜单", to: "/bang" },
                { label: `${creator.name} 形象对比` },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                {creator.platform}
              </span>
              <Link
                to={`/bang?cat=${creator.categorySlug}`}
                className="cursor-pointer rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900 transition-colors hover:bg-accent-200"
              >
                {creator.category}
              </Link>
              <span className="text-xs text-foreground-500">{creator.handle}</span>
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {creator.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {creator.summary}
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                拖动查看前后变化
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                左边是 {creator.beforeYear} 年的公开画面，右边是 {creator.afterYear} 年的最近状态。
              </p>
              <div className="mt-5">
                <CompareSlider
                  beforeImage={creator.beforeImage}
                  afterImage={creator.afterImage}
                  beforeLabel={`${creator.beforeYear} 早期`}
                  afterLabel={`${creator.afterYear} 现在`}
                  name={creator.name}
                />
              </div>
            </div>

            <aside className="flex flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">档案数据</h2>
                <dl className="mt-4 flex flex-col divide-y divide-background-200">
                  {stats.map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-3">
                      <dt className="text-sm text-foreground-600">{item.label}</dt>
                      <dd className="font-heading text-base font-bold text-foreground-950">
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-lg border border-accent-300 bg-accent-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                  <i className="ri-lightbulb-flash-line"></i>
                  变化速览
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  从 {creator.beforeYear} 到 {creator.afterYear}，这组对比主要围绕
                  {creator.tags.join("、")}展开，反差指数为 {creator.contrastScore}，在同类收录对象中
                  {creator.contrastScore >= 85 ? "属于变化相当明显的一组" : "属于稳步变化的一组"}。
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {creator.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-accent-300 bg-background-50 px-2.5 py-1 text-xs text-accent-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              形象变化时间线
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              把这几年公开出现的关键节点按顺序排开，变化其实是一步一步发生的，不是某一夜之间完成的。
            </p>

            <ol className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {creator.timeline.map((node, index) => (
                <li
                  key={`${node.year}-${node.title}`}
                  className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {index + 1}
                    </span>
                    <span className="font-heading text-lg font-black text-foreground-950">
                      {node.year}
                    </span>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                    {node.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{node.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                你可能还想看
              </h2>
              <Link
                to="/bang"
                className="cursor-pointer whitespace-nowrap text-sm text-primary-600 hover:text-primary-700"
              >
                返回全部榜单
              </Link>
            </div>
            <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <CompareCard key={item.slug} creator={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}